import React, { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  LinearProgress,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import useStore from '../store/useStore';
import { PetalField } from '../components/CherryMark';
import CoreMark from '../components/CoreMark';
import Faq from '../components/Faq';
import useScrollReveal from '../hooks/useScrollReveal';
import { orchard } from '../theme';

/* Readable text color for a given background */
function textOn(hex) {
  const c = hex.replace('#', '');
  if (c.length !== 6) return '#fff';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#241E2B' : '#FFFFFF';
}

function tint(hex, alpha) {
  const c = hex.replace('#', '');
  if (c.length !== 6) return hex;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const SWATCHES = [
  { name: 'Cherry', hex: '#C2233E' },
  { name: 'Ocean', hex: '#155E9C' },
  { name: 'Forest', hex: '#1F7A4D' },
  { name: 'Plum', hex: '#6D28D9' },
  { name: 'Sunset', hex: '#D97706' },
  { name: 'Slate', hex: '#334155' },
];

const copy = {
  en: {
    back: 'CORE',
    signIn: 'Sign in',
    enterSaas: 'Step inside the real platform',
    enterSaasBusy: 'Opening the demo cabinet…',
    enterSaasHint:
      'Opens a live CORE cabinet pre-filled with sample students, groups, assessments and reports — no sign-up.',
    aboutCore: 'About the product',
    navDemo: 'Live demo',
    navIncluded: 'What you get',
    navSteps: 'Launch',
    navFaq: 'FAQ',
    tabTeacher: 'Teacher cabinet',
    tabParent: 'Parent report',
    prTitle: 'Progress report · May',
    prStudent: 'Alisa · Movers',
    prBefore: 'Was · September',
    prAfter: 'Now · May',
    prGrowth: '▲ +24 new words this season',
    prSign: 'With care,',
    whyEyebrow: 'Why studios switch',
    whyTitle: 'The same studio, two ways to run it',
    beforeTitle: 'Without CORE',
    beforeItems: [
      'An Excel sheet for every group',
      'Scores scattered across chats and notebooks',
      'Reports written by hand on Sunday night',
      'Parents keep asking “how is she doing?”',
    ],
    afterTitle: 'With CORE',
    afterItems: [
      'One living profile per student',
      'Scores are taps during the lesson',
      'A bilingual PDF report in one click',
      'Parents watch the growth every week',
    ],
    faqEyebrow: 'Straight answers',
    faq: [
      {
        q: 'How much does the white-label cost?',
        a: 'It depends on the size of your school and what we brand for you. We give an exact figure at the walkthrough — after we see your student count and workflow. The demo above costs nothing.',
      },
      {
        q: 'How fast can we launch?',
        a: 'Days, not months. You send a logo and a color, we deploy your instance, import your student list and hand you the keys.',
      },
      {
        q: 'Whose data is it?',
        a: 'Yours. Your students live in your isolated space, and if we ever part ways you get a full export. We never mix partner data.',
      },
      {
        q: 'Do we need our own server or IT person?',
        a: 'No. We host, update and back up the platform. Your own domain plugs in on top.',
      },
      {
        q: 'Can you adapt it to how we teach?',
        a: 'Templates are already free-form, and you get a direct line to the team that builds the product — real requests genuinely shape the roadmap.',
      },
      {
        q: 'What will parents see?',
        a: 'Only your brand: your name in the app, your letterhead on every PDF, your domain in the address bar. CORE stays invisible.',
      },
    ],
    eyebrow: 'White-label program',
    heroA: 'Your brand.',
    heroB: 'Our engine.',
    heroSub:
      'Launch a student-profiling platform under your school’s own name — profiles, assessments, attendance analytics and bilingual parent reports, all wearing your colors.',
    tryDemo: 'Try the live demo',
    contact: 'Book a walkthrough',
    demoEyebrow: 'The live demo',
    demoTitle: 'Type your name. Pick your color. Watch it become yours.',
    demoHint:
      'Everything below re-brands in real time — exactly what your teachers and parents would see.',
    studioName: 'Your studio name',
    brandColor: 'Brand color',
    custom: 'Custom',
    demoStudents: [
      { name: 'Alisa M.', level: 'Movers · A1', progress: 78 },
      { name: 'Timur K.', level: 'Starters', progress: 62 },
      { name: 'Sofia R.', level: 'Flyers · A2', progress: 91 },
    ],
    demoNav: ['Dashboard', 'Students', 'Reports'],
    demoChartLabel: 'Group progress · spring season',
    demoReportTitle: 'Parent report · May',
    demoReportLine: 'Vocabulary growth framed positively, in two languages.',
    demoFoonote: 'Generated by the platform — signed by your studio.',
    includedEyebrow: 'What partners get',
    included: [
      {
        title: 'Your identity everywhere',
        text: 'Logo, colors and tone across the app, the parent portal and every PDF page.',
      },
      {
        title: 'Bilingual parent reports',
        text: 'English–Russian growth reports that carry your letterhead, not ours.',
      },
      {
        title: 'Own domain & login',
        text: 'Teachers sign in at your address. CORE stays invisible.',
      },
      {
        title: 'Isolated data',
        text: 'Your students live in your space. No sharing, no co-mingling.',
      },
      {
        title: 'Onboarding done for you',
        text: 'We import your student list and templates before launch day.',
      },
      {
        title: 'A partner, not a vendor',
        text: 'Direct line to the team that builds the product you resell.',
      },
    ],
    stepsEyebrow: 'From handshake to launch',
    steps: [
      {
        title: 'Share your brand kit',
        text: 'A logo and a color are enough — we handle the rest.',
      },
      {
        title: 'We deploy your instance',
        text: 'Your domain, your look, your language defaults, seeded and tested.',
      },
      {
        title: 'You open the doors',
        text: 'Teachers profile students on day one; parents get reports the first week.',
      },
    ],
    ctaTitle: 'Put your name on the door.',
    ctaText:
      'Send us a note and we’ll return a live, branded demo of your studio within days — like the one above, but real.',
  },
  ru: {
    back: 'CORE',
    signIn: 'Войти',
    enterSaas: 'Войти в настоящий кабинет',
    enterSaasBusy: 'Открываем демо-кабинет…',
    enterSaasHint:
      'Откроется живой кабинет CORE с примерами: 6 учеников, 2 группы, оценки, посещаемость и отчёты — без регистрации.',
    aboutCore: 'О продукте',
    navDemo: 'Живое демо',
    navIncluded: 'Что входит',
    navSteps: 'Запуск',
    navFaq: 'Вопросы',
    tabTeacher: 'Кабинет учителя',
    tabParent: 'Отчёт родителя',
    prTitle: 'Отчёт о прогрессе · Май',
    prStudent: 'Алиса · Movers',
    prBefore: 'Было · Сентябрь',
    prAfter: 'Стало · Май',
    prGrowth: '▲ +24 новых слова за сезон',
    prSign: 'С заботой,',
    whyEyebrow: 'Почему студии переходят',
    whyTitle: 'Одна и та же студия — два способа работать',
    beforeTitle: 'Без CORE',
    beforeItems: [
      'Таблица Excel на каждую группу',
      'Оценки разбросаны по чатам и тетрадям',
      'Отчёты пишутся вручную в воскресенье вечером',
      'Родители снова спрашивают: «как у нас дела?»',
    ],
    afterTitle: 'С CORE',
    afterItems: [
      'Один живой профиль на ученика',
      'Оценки — касания прямо на уроке',
      'Двуязычный PDF-отчёт в один клик',
      'Родители видят рост каждую неделю',
    ],
    faqEyebrow: 'Честные ответы',
    faq: [
      {
        q: 'Сколько стоит white-label?',
        a: 'Зависит от размера школы и объёма брендирования. Точную цифру называем на показе — когда увидим количество учеников и ваши процессы. Демо выше — бесплатно.',
      },
      {
        q: 'Как быстро можно запуститься?',
        a: 'Дни, а не месяцы. Вы присылаете логотип и цвет, мы разворачиваем вашу копию, импортируем список учеников и отдаём ключи.',
      },
      {
        q: 'Чьи это данные?',
        a: 'Ваши. Ученики живут в вашем изолированном пространстве, а при расставании вы получаете полную выгрузку. Данные партнёров никогда не смешиваются.',
      },
      {
        q: 'Нужен ли свой сервер или IT-специалист?',
        a: 'Нет. Мы хостим, обновляем и делаем резервные копии. Ваш собственный домен подключается сверху.',
      },
      {
        q: 'Можно ли адаптировать под то, как мы преподаём?',
        a: 'Шаблоны и так свободные, а у вас будет прямая связь с командой, которая делает продукт — реальные запросы действительно попадают в план.',
      },
      {
        q: 'Что увидят родители?',
        a: 'Только ваш бренд: ваше имя в приложении, ваш бланк на каждом PDF, ваш домен в адресной строке. CORE остаётся невидимым.',
      },
    ],
    eyebrow: 'White-label программа',
    heroA: 'Ваш бренд.',
    heroB: 'Наш движок.',
    heroSub:
      'Запустите платформу профилей учеников под именем вашей школы — профили, оценки, аналитика посещаемости и двуязычные отчёты для родителей в ваших цветах.',
    tryDemo: 'Живая демонстрация',
    contact: 'Записаться на показ',
    demoEyebrow: 'Живое демо',
    demoTitle: 'Введите название. Выберите цвет. Платформа станет вашей.',
    demoHint:
      'Всё ниже перекрашивается в реальном времени — именно это увидят ваши учителя и родители.',
    studioName: 'Название вашей студии',
    brandColor: 'Фирменный цвет',
    custom: 'Свой',
    demoStudents: [
      { name: 'Алиса М.', level: 'Movers · A1', progress: 78 },
      { name: 'Тимур К.', level: 'Starters', progress: 62 },
      { name: 'София Р.', level: 'Flyers · A2', progress: 91 },
    ],
    demoNav: ['Главная', 'Ученики', 'Отчёты'],
    demoChartLabel: 'Прогресс группы · весенний сезон',
    demoReportTitle: 'Отчёт для родителей · Май',
    demoReportLine: 'Рост словарного запаса — позитивно и на двух языках.',
    demoFoonote: 'Создано платформой — подписано вашей студией.',
    includedEyebrow: 'Что получают партнёры',
    included: [
      {
        title: 'Ваша айдентика везде',
        text: 'Логотип, цвета и тон — в приложении, портале родителей и каждом PDF.',
      },
      {
        title: 'Двуязычные отчёты',
        text: 'Англо-русские отчёты о росте на вашем бланке, а не нашем.',
      },
      {
        title: 'Свой домен и вход',
        text: 'Учителя заходят по вашему адресу. CORE остаётся невидимым.',
      },
      {
        title: 'Изолированные данные',
        text: 'Ваши ученики живут в вашем пространстве. Без смешивания.',
      },
      {
        title: 'Онбординг под ключ',
        text: 'Импортируем список учеников и шаблоны до дня запуска.',
      },
      {
        title: 'Партнёр, а не подрядчик',
        text: 'Прямая связь с командой, которая делает продукт.',
      },
    ],
    stepsEyebrow: 'От рукопожатия до запуска',
    steps: [
      {
        title: 'Пришлите брендбук',
        text: 'Достаточно логотипа и цвета — остальное сделаем мы.',
      },
      {
        title: 'Разворачиваем вашу копию',
        text: 'Ваш домен, ваш стиль, ваши языковые настройки — всё протестировано.',
      },
      {
        title: 'Вы открываете двери',
        text: 'Учителя ведут профили с первого дня; родители получают отчёты в первую неделю.',
      },
    ],
    ctaTitle: 'Повесьте свою вывеску.',
    ctaText:
      'Напишите нам — и через несколько дней вы получите живое брендированное демо вашей студии, как выше, только настоящее.',
  },
};

const eyebrowSx = {
  fontFamily: orchard.fontMono,
  fontSize: 12,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: orchard.cherry,
  display: 'block',
  mb: 2,
};

/* ── The live-rebranding demo dashboard ─────────────────────── */
function DemoDashboard({ brand, name, t }) {
  const onBrand = textOn(brand);
  const bars = [42, 58, 50, 66, 74, 88];
  return (
    <Box
      sx={{
        borderRadius: 5,
        overflow: 'hidden',
        border: '1px solid rgba(36,30,43,0.1)',
        boxShadow: `0 40px 80px -32px ${tint(brand, 0.45)}`,
        bgcolor: '#fff',
        transition: 'box-shadow 0.4s ease',
      }}
    >
      {/* Fake appbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2.5,
          py: 1.5,
          bgcolor: brand,
          color: onBrand,
          transition: 'background 0.35s ease, color 0.35s ease',
        }}
      >
        {/* Client "logo": studio initial — this is their brand, not ours */}
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: onBrand,
            color: brand,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: orchard.fontDisplay,
            fontWeight: 700,
            fontSize: 12,
            flexShrink: 0,
            transition: 'background 0.35s ease, color 0.35s ease',
          }}
        >
          {name.trim().charAt(0).toUpperCase() || 'C'}
        </Box>
        <Typography
          sx={{
            fontFamily: orchard.fontDisplay,
            fontWeight: 600,
            fontSize: '0.9rem',
            mr: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </Typography>
        {t.demoNav.map((n, i) => (
          <Typography
            key={n}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              opacity: i === 0 ? 1 : 0.65,
              display: { xs: i > 1 ? 'none' : 'block', sm: 'block' },
            }}
          >
            {n}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          p: 2.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1fr' },
          gap: 2.5,
        }}
      >
        {/* Students column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {t.demoStudents.map((s) => (
            <Box
              key={s.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 3,
                border: '1px solid rgba(36,30,43,0.08)',
                bgcolor: tint(brand, 0.04),
                transition: 'background 0.35s ease',
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: tint(brand, 0.15),
                  color: brand,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 13,
                  transition: 'background 0.35s ease, color 0.35s ease',
                  flexShrink: 0,
                }}
              >
                {s.name.slice(0, 1)}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                  {s.name}
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.5 }}
                >
                  {s.level}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={s.progress}
                  sx={{
                    height: 6,
                    bgcolor: 'rgba(36,30,43,0.06)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: brand,
                      transition: 'background 0.35s ease',
                    },
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: orchard.fontMono,
                  fontSize: 12,
                  fontWeight: 700,
                  color: brand,
                  transition: 'color 0.35s ease',
                }}
              >
                {s.progress}%
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Chart + report column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(36,30,43,0.08)',
            }}
          >
            <Typography
              sx={{
                fontFamily: orchard.fontMono,
                fontSize: 10.5,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 1.5,
              }}
            >
              {t.demoChartLabel}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
                height: 84,
              }}
            >
              {bars.map((h, i) => (
                <Box
                  key={i}
                  sx={{
                    flexGrow: 1,
                    height: `${h}%`,
                    borderRadius: '6px 6px 2px 2px',
                    bgcolor: i === bars.length - 1 ? brand : tint(brand, 0.25),
                    transition: 'background 0.35s ease, height 0.5s ease',
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: tint(brand, 0.08),
              border: `1px dashed ${tint(brand, 0.5)}`,
              transition: 'background 0.35s ease, border-color 0.35s ease',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
              {t.demoReportTitle}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
              {t.demoReportLine}
            </Typography>
            <Typography
              sx={{
                fontFamily: orchard.fontMono,
                fontSize: 10.5,
                mt: 1,
                color: brand,
                transition: 'color 0.35s ease',
              }}
            >
              {name} · PDF
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: '1px solid rgba(36,30,43,0.06)',
          bgcolor: 'rgba(36,30,43,0.02)',
        }}
      >
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
          {t.demoFoonote}
        </Typography>
      </Box>
    </Box>
  );
}

/* ── Parent report preview — an A4-ish page wearing the client's brand ── */
function ParentReportMock({ brand, name, t }) {
  const onBrand = textOn(brand);
  return (
    <Box
      sx={{
        maxWidth: 460,
        mx: 'auto',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid rgba(36,30,43,0.1)',
        boxShadow: `0 40px 80px -32px ${tint(brand, 0.45)}`,
        bgcolor: '#fff',
        transition: 'box-shadow 0.4s ease',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: brand,
          color: onBrand,
          transition: 'background 0.35s ease, color 0.35s ease',
        }}
      >
        <Typography
          sx={{
            fontFamily: orchard.fontDisplay,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          {name}
        </Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
          {t.prTitle}
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14.5, mb: 2 }}>
          {t.prStudent}
        </Typography>
        {[
          { label: t.prBefore, pct: 34, color: 'rgba(36,30,43,0.25)' },
          { label: t.prAfter, pct: 78, color: brand },
        ].map((row, i) => (
          <Box key={row.label} sx={{ mb: 1.75 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
            >
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {row.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: orchard.fontMono,
                  fontSize: 12,
                  fontWeight: 700,
                  color: i === 1 ? brand : 'text.secondary',
                  transition: 'color 0.35s ease',
                }}
              >
                {row.pct}%
              </Typography>
            </Box>
            <Box
              sx={{
                height: 9,
                borderRadius: 99,
                bgcolor: 'rgba(36,30,43,0.06)',
                overflow: 'hidden',
              }}
            >
              <Box
                className="cp-bar"
                style={{ '--cp-bar-w': `${row.pct}%` }}
                sx={{
                  height: '100%',
                  borderRadius: 99,
                  bgcolor: row.color,
                  transition:
                    'width 1.1s cubic-bezier(0.22,1,0.36,1) 0.25s, background 0.35s ease',
                  transitionDelay: `${0.25 + i * 0.2}s`,
                }}
              />
            </Box>
          </Box>
        ))}
        <Box
          sx={{
            mt: 2.5,
            display: 'inline-block',
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            bgcolor: 'rgba(46,125,91,0.1)',
          }}
        >
          <Typography
            sx={{
              fontFamily: orchard.fontMono,
              fontSize: 11.5,
              fontWeight: 700,
              color: orchard.leaf,
            }}
          >
            {t.prGrowth}
          </Typography>
        </Box>
        <Typography
          sx={{
            mt: 2.5,
            fontSize: 12.5,
            fontStyle: 'italic',
            color: 'text.secondary',
          }}
        >
          {t.prSign}{' '}
          <Box
            component="span"
            sx={{
              color: brand,
              fontWeight: 700,
              transition: 'color 0.35s ease',
            }}
          >
            {name}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

export default function WhiteLabelPage() {
  const { language, setLanguage, login } = useStore();
  const t = copy[language] || copy.en;
  const rootRef = useScrollReveal();
  const navigate = useNavigate();
  const [brand, setBrand] = useState('#155E9C');
  const [name, setName] = useState('');
  const [demoBusy, setDemoBusy] = useState(false);
  const [view, setView] = useState('teacher');
  const demoRef = useRef(null);

  async function enterDemo() {
    if (demoBusy) return;
    setDemoBusy(true);
    try {
      const res = await fetch('/api/auth/demo', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        login(data.role, data.user);
        navigate('/');
        return;
      }
    } catch (err) {
      /* server unreachable — fall through to reset */
    }
    setDemoBusy(false);
  }

  const studioName =
    name.trim() || (language === 'ru' ? 'Ваша студия' : 'Your Studio');
  const mailto = `mailto:abmkf96@gmail.com?subject=${encodeURIComponent(
    'Cherry-Pick white-label demo'
  )}`;

  return (
    <Box
      ref={rootRef}
      sx={{
        bgcolor: orchard.paper,
        color: orchard.ink,
        minHeight: '100vh',
        overflowX: 'hidden',
      }}
    >
      {/* ── Top bar ── */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          bgcolor: 'rgba(251,248,245,0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(36,30,43,0.08)',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25 }}
        >
          <Box
            component={RouterLink}
            to="/welcome"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit',
              mr: 'auto',
            }}
          >
            <CoreMark size={28} />
            <Typography
              sx={{
                fontFamily: orchard.fontDisplay,
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {t.back}
            </Typography>
            <Typography
              sx={{
                fontFamily: orchard.fontMono,
                fontSize: 11,
                letterSpacing: '0.14em',
                color: 'text.secondary',
                ml: 0.5,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              / PARTNERS
            </Typography>
          </Box>
          <Box
            component="nav"
            aria-label="Sections"
            sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, mr: 1 }}
          >
            {[
              { href: '#demo', label: t.navDemo },
              { href: '#included', label: t.navIncluded },
              { href: '#steps', label: t.navSteps },
              { href: '#faq', label: t.navFaq },
            ].map((l) => (
              <Box
                key={l.href}
                component="a"
                href={l.href}
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.secondary',
                  textDecoration: 'none',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  transition: 'color 0.18s ease',
                  '&:hover': { color: orchard.cherry },
                }}
              >
                {l.label}
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<TranslateIcon />}
            onClick={() => setLanguage(language === 'en' ? 'ru' : 'en')}
            sx={{ color: 'text.secondary' }}
          >
            {(language || 'en').toUpperCase()}
          </Button>
          <Button size="small" variant="contained" href={mailto}>
            {t.contact}
          </Button>
        </Container>
      </Box>

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative' }}>
        <PetalField />
        <Container
          maxWidth="md"
          sx={{
            pt: { xs: 8, md: 11 },
            pb: { xs: 7, md: 9 },
            textAlign: 'center',
          }}
        >
          <Box className="cp-stagger">
            <Typography component="span" sx={eyebrowSx}>
              {t.eyebrow}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: orchard.fontDisplay,
                fontWeight: 700,
                fontSize: { xs: '2.1rem', sm: '2.9rem', md: '3.4rem' },
                lineHeight: 1.12,
                letterSpacing: '-0.02em',
              }}
            >
              {t.heroA}{' '}
              <Box component="span" sx={{ color: orchard.cherry }}>
                {t.heroB}
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: 3,
                mx: 'auto',
                fontSize: '1.1rem',
                lineHeight: 1.65,
                color: 'text.secondary',
                maxWidth: 640,
              }}
            >
              {t.heroSub}
            </Typography>
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                gap: 1.5,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowDownwardIcon />}
                onClick={() =>
                  demoRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
              >
                {t.tryDemo}
              </Button>
              <Button
                variant="outlined"
                size="large"
                href={mailto}
                startIcon={<MailOutlineIcon />}
              >
                {t.contact}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Live rebrand demo ── */}
      <Box
        id="demo"
        ref={demoRef}
        sx={{
          bgcolor: '#fff',
          borderTop: '1px solid rgba(36,30,43,0.06)',
          scrollMarginTop: 80,
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
          <Box className="cp-reveal" sx={{ maxWidth: 720 }}>
            <Typography component="span" sx={eyebrowSx}>
              {t.demoEyebrow}
            </Typography>
            <Typography
              sx={{
                fontFamily: orchard.fontDisplay,
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '2rem' },
                lineHeight: 1.28,
              }}
            >
              {t.demoTitle}
            </Typography>
            <Typography sx={{ mt: 1.5, color: 'text.secondary' }}>
              {t.demoHint}
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 5,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
              gap: { xs: 4, md: 5 },
              alignItems: 'start',
            }}
          >
            {/* Controls */}
            <Box
              className="cp-reveal"
              sx={{
                p: 3,
                borderRadius: 4,
                border: '1px solid rgba(36,30,43,0.1)',
                bgcolor: orchard.paper,
                position: { md: 'sticky' },
                top: { md: 96 },
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1.5, fontSize: 14 }}>
                {t.studioName}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={
                  language === 'ru'
                    ? 'Например: Лингва Кидс'
                    : 'e.g. Lingua Kids'
                }
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 28 }}
              />
              <Typography
                sx={{ fontWeight: 800, mt: 3, mb: 1.5, fontSize: 14 }}
              >
                {t.brandColor}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {SWATCHES.map((s) => (
                  <Box
                    key={s.hex}
                    component="button"
                    type="button"
                    aria-label={s.name}
                    onClick={() => setBrand(s.hex)}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: s.hex,
                      cursor: 'pointer',
                      border:
                        brand === s.hex
                          ? `3px solid ${orchard.ink}`
                          : '3px solid transparent',
                      outlineOffset: 2,
                      transition: 'transform 0.15s ease',
                      '&:hover': { transform: 'scale(1.12)' },
                      animation:
                        brand === s.hex
                          ? 'cpPulseRing 1.6s ease-out 1'
                          : 'none',
                    }}
                  />
                ))}
                <Box
                  component="label"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    border: '2px dashed rgba(36,30,43,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 9,
                    fontFamily: orchard.fontMono,
                    color: 'text.secondary',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {t.custom}
                  <Box
                    component="input"
                    type="color"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: 0,
                      cursor: 'pointer',
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 3,
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: tint(brand, 0.1),
                  transition: 'background 0.35s ease',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: orchard.fontMono,
                    fontSize: 11,
                    color: 'text.secondary',
                  }}
                >
                  {studioName} · {brand.toUpperCase()}
                </Typography>
              </Box>
            </Box>

            {/* Live preview with teacher/parent tabs */}
            <Box className="cp-reveal" sx={{ transitionDelay: '0.12s' }}>
              <Box
                role="tablist"
                aria-label={t.demoEyebrow}
                sx={{
                  display: 'inline-flex',
                  gap: 0.5,
                  p: 0.5,
                  mb: 2.5,
                  borderRadius: 99,
                  border: '1px solid rgba(36,30,43,0.12)',
                  bgcolor: '#fff',
                }}
              >
                {[
                  { key: 'teacher', label: t.tabTeacher },
                  { key: 'parent', label: t.tabParent },
                ].map((tab) => {
                  const active = view === tab.key;
                  return (
                    <Box
                      key={tab.key}
                      component="button"
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setView(tab.key)}
                      sx={{
                        all: 'unset',
                        cursor: 'pointer',
                        px: 2.25,
                        py: 0.9,
                        borderRadius: 99,
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: active ? textOn(brand) : orchard.ink,
                        bgcolor: active ? brand : 'transparent',
                        transition: 'background 0.25s ease, color 0.25s ease',
                        '&:focus-visible': {
                          outline: `2px solid ${brand}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {tab.label}
                    </Box>
                  );
                })}
              </Box>
              {view === 'teacher' ? (
                <DemoDashboard brand={brand} name={studioName} t={t} />
              ) : (
                <ParentReportMock brand={brand} name={studioName} t={t} />
              )}
            </Box>
          </Box>

          {/* Enter the real SaaS */}
          <Box className="cp-reveal" sx={{ mt: 6, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={enterDemo}
              disabled={demoBusy}
              endIcon={<PlayArrowIcon />}
              sx={{ px: 4, py: 1.6, fontSize: '1rem' }}
            >
              {demoBusy ? t.enterSaasBusy : t.enterSaas}
            </Button>
            <Typography
              sx={{
                mt: 1.5,
                mx: 'auto',
                maxWidth: 520,
                fontSize: 13.5,
                color: 'text.secondary',
              }}
            >
              {t.enterSaasHint}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Before / after ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
        <Box className="cp-reveal" sx={{ maxWidth: 720 }}>
          <Typography component="span" sx={eyebrowSx}>
            {t.whyEyebrow}
          </Typography>
          <Typography
            className="cp-balance"
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '2rem' },
              lineHeight: 1.28,
            }}
          >
            {t.whyTitle}
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          <Box
            className="cp-reveal"
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              border: '1px dashed rgba(36,30,43,0.25)',
              bgcolor: 'transparent',
            }}
          >
            <Typography
              sx={{
                fontFamily: orchard.fontMono,
                fontSize: 12,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 2,
              }}
            >
              {t.beforeTitle}
            </Typography>
            {t.beforeItems.map((item) => (
              <Box
                key={item}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  mb: 1.5,
                  alignItems: 'baseline',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: orchard.fontMono,
                    fontSize: 13,
                    color: 'rgba(36,30,43,0.35)',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </Box>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            className="cp-reveal"
            sx={{
              transitionDelay: '0.12s',
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              border: '1px solid rgba(194,35,62,0.25)',
              bgcolor: '#fff',
              boxShadow: '0 24px 48px -24px rgba(140,18,48,0.2)',
            }}
          >
            <Typography
              sx={{
                fontFamily: orchard.fontMono,
                fontSize: 12,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: orchard.cherry,
                mb: 2,
              }}
            >
              {t.afterTitle}
            </Typography>
            {t.afterItems.map((item) => (
              <Box
                key={item}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  mb: 1.5,
                  alignItems: 'baseline',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: orchard.fontMono,
                    fontSize: 13,
                    color: orchard.leaf,
                    flexShrink: 0,
                    fontWeight: 700,
                  }}
                >
                  ✓
                </Box>
                <Typography sx={{ lineHeight: 1.55, fontWeight: 500 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      {/* ── What's included ── */}
      <Box
        id="included"
        sx={{
          bgcolor: '#fff',
          borderTop: '1px solid rgba(36,30,43,0.06)',
          scrollMarginTop: 72,
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
          <Typography component="span" sx={eyebrowSx} className="cp-reveal">
            {t.includedEyebrow}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
              mt: 2,
            }}
          >
            {t.included.map((f, i) => (
              <Box
                key={f.title}
                className="cp-reveal"
                sx={{
                  transitionDelay: `${(i % 3) * 0.1}s`,
                  p: 3.5,
                  borderRadius: 4,
                  bgcolor: orchard.paper,
                  border: '1px solid rgba(36,30,43,0.08)',
                  transition:
                    'transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s ease, border-color 0.25s ease, opacity 0.7s cubic-bezier(0.22,1,0.36,1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(194,35,62,0.35)',
                    boxShadow: '0 22px 44px -20px rgba(140,18,48,0.25)',
                  },
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                  {f.title}
                </Typography>
                <Typography
                  sx={{
                    mt: 1.25,
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                  }}
                >
                  {f.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Steps ── */}
      <Box id="steps" sx={{ scrollMarginTop: 72 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
          <Typography component="span" sx={eyebrowSx} className="cp-reveal">
            {t.stepsEyebrow}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 4, md: 5 },
              mt: 2,
            }}
          >
            {t.steps.map((s, i) => (
              <Box
                key={s.title}
                className="cp-reveal"
                sx={{ transitionDelay: `${i * 0.12}s` }}
              >
                <Typography
                  sx={{
                    fontFamily: orchard.fontMono,
                    fontSize: 13,
                    fontWeight: 700,
                    color: orchard.cherry,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: orchard.fontDisplay,
                    fontWeight: 600,
                    fontSize: '1.2rem',
                    mt: 1,
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.65 }}
                >
                  {s.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box
        id="faq"
        sx={{
          bgcolor: '#fff',
          borderTop: '1px solid rgba(36,30,43,0.06)',
          scrollMarginTop: 72,
        }}
      >
        <Container maxWidth="md" sx={{ py: { xs: 8, md: 11 } }}>
          <Typography component="span" sx={eyebrowSx} className="cp-reveal">
            {t.navFaq}
          </Typography>
          <Typography
            className="cp-reveal cp-balance"
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '1.9rem' },
              lineHeight: 1.3,
              mb: 4,
            }}
          >
            {t.faqEyebrow}
          </Typography>
          <Box className="cp-reveal" sx={{ transitionDelay: '0.1s' }}>
            <Faq items={t.faq} />
          </Box>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ bgcolor: orchard.night, position: 'relative' }}>
        <PetalField tint="rgba(242,89,114,0.25)" />
        <Container
          maxWidth="md"
          sx={{ py: { xs: 9, md: 12 }, textAlign: 'center' }}
        >
          <Box className="cp-reveal">
            <Typography
              sx={{
                fontFamily: orchard.fontDisplay,
                fontWeight: 700,
                fontSize: { xs: '1.7rem', md: '2.3rem' },
                color: '#F4EEF6',
                lineHeight: 1.25,
              }}
            >
              {t.ctaTitle}
            </Typography>
            <Typography
              sx={{
                mt: 2,
                color: 'rgba(244,238,246,0.7)',
                fontSize: '1.05rem',
                lineHeight: 1.65,
              }}
            >
              {t.ctaText}
            </Typography>
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                gap: 1.5,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                size="large"
                href={mailto}
                endIcon={<ArrowForwardIcon />}
              >
                {t.contact}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={enterDemo}
                disabled={demoBusy}
                endIcon={<PlayArrowIcon />}
                sx={{
                  color: '#F4EEF6',
                  borderColor: 'rgba(244,238,246,0.4)',
                  '&:hover': { borderColor: '#F4EEF6' },
                }}
              >
                {demoBusy ? t.enterSaasBusy : t.enterSaas}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: orchard.night, pb: 4 }}>
        <Container
          maxWidth="lg"
          sx={{
            borderTop: '1px solid rgba(244,238,246,0.1)',
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CoreMark
            size={20}
            core={orchard.cherryBright}
            rings="rgba(244,238,246,0.7)"
          />
          <Typography sx={{ color: 'rgba(244,238,246,0.55)', fontSize: 13 }}>
            © {new Date().getFullYear()} CORE · made with love by{' '}
            <Box
              component="a"
              href="https://klicka.ru"
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'inherit' }}
            >
              klicka.ru
            </Box>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
